import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { isDomainLikeName } from "@/lib/businessName";

interface Props {
  clientId: string;
  /** The current value of clients.business_name (the dedicated entity field). */
  currentBusinessName: string | null;
  /** Fallback display: clients.name (may be a domain or "My Website"). */
  fallbackName?: string | null;
  onSaved: (name: string) => void;
}

const BusinessNamePrompt = ({ clientId, currentBusinessName, fallbackName, onSaved }: Props) => {
  const [valueEn, setValueEn] = useState("");
  const [valueFr, setValueFr] = useState("");
  const [saving, setSaving] = useState(false);

  // Only prompt when business_name is genuinely missing. Once it's set to a
  // valid human-readable value, we never re-ask.
  const hasValid = !!currentBusinessName && currentBusinessName.trim() !== "" &&
    !isDomainLikeName(currentBusinessName) &&
    currentBusinessName.trim().toLowerCase() !== "my website";
  if (hasValid) return null;

  const display = currentBusinessName || fallbackName || "(none set)";

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const en = valueEn.trim();
    const fr = valueFr.trim();
    if (!en) {
      toast({ title: "Please enter your English business name", variant: "destructive" });
      return;
    }
    if (isDomainLikeName(en) || (fr && isDomainLikeName(fr))) {
      toast({
        title: "That looks like a domain",
        description: "Enter your real business name (e.g. Montreal Locksmith / Serrurier Montréal).",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    const i18n: Record<string, string> = { en };
    if (fr) i18n.fr = fr;
    const { error } = await supabase
      .from("clients")
      .update({ business_name: en, business_name_i18n: i18n })
      .eq("id", clientId);
    setSaving(false);
    if (error) {
      toast({ title: "Couldn't save", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Business name saved" });
    onSaved(en);
  };

  return (
    <div className="mb-6 rounded-[16px] border border-primary/30 bg-primary/5 p-5">
      <div className="mb-2 text-[13px] font-[700] uppercase tracking-[0.12em] text-primary">
        Confirm your business name
      </div>
      <p className="mb-4 text-[14px] text-foreground/80">
        We need your real human-readable business name for entity linking, brand
        mentions, and local SEO consistency. The French version is used inside
        French articles so the brand anchor matches the article language.
        Current value: <span className="font-[600]">{display}</span>.
      </p>
      <form onSubmit={save} className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={valueEn}
            onChange={(e) => setValueEn(e.target.value)}
            placeholder="English name (e.g. Montreal Locksmith)"
            required
            className="h-11 flex-1 rounded-[10px] border border-input bg-background px-4 text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
          />
          <input
            type="text"
            value={valueFr}
            onChange={(e) => setValueFr(e.target.value)}
            placeholder="French name (e.g. Serrurier Montréal) — optional"
            className="h-11 flex-1 rounded-[10px] border border-input bg-background px-4 text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="h-11 self-start rounded-[10px] bg-primary px-5 text-[14px] font-[700] text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save name"}
        </button>
      </form>
    </div>
  );
};

export default BusinessNamePrompt;
